import { CopyableTypography } from '@src/common/components/CopyableTypography';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { Timestamp } from '@src/common/time/Timestamp';
import type { Order } from '@src/orders/domain/Order';
import type {
  EntryValue,
  PermitApplication,
} from '@src/orders/domain/PermitApplication';
import { usePermitViewFieldLabels } from '@src/permitField/hooks/usePermitViewFieldLabels';
import {
  PrimaryDeliveryTypeField,
  SecondaryDeliveryTypeField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';

interface SectionProps {
  section: string;
  fields: string[];
  permitApplication: PermitApplication;
  order: Order;
}

type Props = SectionProps & {
  nested?: boolean;
};

const ignoredSectionTitles = ['trips', 'globalTrips', 'trailers'];

export const PermitSection = ({ nested, ...props }: Props) => {
  const { t } = useTranslation();
  const rows = nested
    ? NestedSection(props).map((row, index) =>
        cloneElement(row, { key: index }),
      )
    : RegularSection(props).map((row, index) =>
        cloneElement(row, { key: index }),
      );

  if (rows.length === 0) {
    return;
  }

  return (
    <Stack key={props.section}>
      {!ignoredSectionTitles.includes(props.section) && (
        <Typography variant="body1" className={sectionTitle}>
          {props.section === 'companyAddress'
            ? t('companyAddress.title')
            : t(props.section)}
        </Typography>
      )}
      <Stack style={{ paddingLeft: theme.spacing['3'] }}>{rows}</Stack>
    </Stack>
  );
};

const RegularSection = ({ fields, permitApplication, order }: SectionProps) => {
  const { formatField } = usePermitViewFieldLabels({ permitApplication });

  const fieldsNotToDisplay = [
    PrimaryDeliveryTypeField.fieldName,
    SecondaryDeliveryTypeField.fieldName,
  ];

  return fields
    .filter((field) => !fieldsNotToDisplay.includes(field))
    .map((field) => {
      const rawValue = permitApplication.getFieldValue(field)?.toString() ?? '';
      const { fieldLabel, valueType, value, valueUnit } = formatField({
        field,
        value: rawValue,
      });
      const lastUpdatedDate = order.getLastUpdatedFor(
        field,
        permitApplication.id,
      );
      const lastUpdated = lastUpdatedDate
        ? new Timestamp(lastUpdatedDate).formatUTCDate()
        : undefined;
      return (
        <FieldRow
          id={field}
          key={field}
          label={fieldLabel}
          valueType={valueType}
          value={value}
          lastUpdated={lastUpdated}
          valueUnit={valueUnit}
        />
      );
    });
};

const NestedSection = ({
  section,
  fields,
  permitApplication,
  order,
}: SectionProps) => {
  const { t } = useTranslation();
  const { formatField } = usePermitViewFieldLabels({ permitApplication });

  const fieldValue = permitApplication.getFieldValue(section) as Record<
    string,
    EntryValue
  >[];
  if (!fieldValue) {
    return [];
  }
  return fieldValue.map((entry, index) => (
    <Stack
      key={t(`${section}_section`, { key: index + 1 })}
      marginBottom={theme.spacing['3']}
    >
      <Typography variant="body1" className={sectionTitle}>
        {t(`${section}_section`, { key: index + 1 })}
      </Typography>
      {fields.map((field) => {
        const rawValue = entry[field]?.toString();
        const { fieldLabel, value, valueUnit } = formatField({
          field,
          value: rawValue,
        });
        const lastUpdatedDate = order.getLastUpdatedFor(
          field,
          permitApplication.id,
        );
        const lastUpdated = lastUpdatedDate
          ? new Timestamp(lastUpdatedDate).formatUTCDate()
          : undefined;
        return (
          <FieldRow
            key={field}
            id={field}
            label={fieldLabel}
            value={value}
            lastUpdated={lastUpdated}
            valueUnit={valueUnit}
          />
        );
      })}
    </Stack>
  ));
};

export const FieldRow = ({
  id,
  label,
  valueType,
  value,
  valueUnit,
  lastUpdated,
}: {
  id: string;
  label: string;
  valueType?: string;
  value?: string;
  valueUnit?: string;
  lastUpdated?: string;
}) => (
  <div
    key={id}
    className={sectionField}
    aria-labelledby={`${id}-field`}
    aria-label={label}
    data-testid={`row-${label}${valueType ? `-${valueType}` : ''}`}
  >
    <Stack className={fieldLabel}>
      <Typography variant="body1" id={`${id}-field`} color="secondary">
        {label}:&nbsp;
      </Typography>
    </Stack>

    <Stack direction="row">
      <CopyableTypography
        variant="body1"
        textBefore={valueType ? `${valueType}:` : undefined}
        textAfter={valueUnit ? `${valueUnit}` : undefined}
        paddingY={theme.spacing['1']}
        paddingX={theme.spacing['2']}
        className={valueStyle}
        lastUpdated={lastUpdated}
      >
        {value}
      </CopyableTypography>
    </Stack>
  </div>
);

const sectionTitle = css({
  fontWeight: '500',
  marginBottom: '6px',
  paddingBottom: `${theme.spacing['1']}`,
  paddingTop: `${theme.spacing['2']}`,
});

const sectionField = css({
  display: 'flex',
  flexDirection: 'row',
});

const fieldLabel = css({
  display: 'flex',
  flexDirection: 'row',
  width: '300px',
  marginRight: `${theme.spacing['1']}`,
  padding: `${theme.spacing['1']} 0`,
});

const valueStyle = css({
  fontWeight: '500',
});
